'use client';

/**
 * Cart Canvas View - Full-width cart layout for Canvas
 *
 * @component_role Canvas Cart Layout
 * @business_rule Displays cart in a spacious grid layout when expanded to Canvas.
 *   Products shown as cards in responsive grid, RFQ form below.
 * @user_outcome Review large carts comfortably with full-width layout.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain procurement
 */

import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import {
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Send,
  Loader2,
  Package,
  FileText,
  Calendar,
  Users,
  FolderOpen,
  X,
  RefreshCw,
} from 'lucide-react';
import { useCartStore, type CartItem, getItemPrice, getItemVendorName } from '@/lib/stores/cart-store';
import { useCreateRFQ, useProjects, useVendors } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

export function CartCanvasView() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Dropdown states
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);

  // Cart state - items
  // Select actual state slice for proper Zustand reactivity
  const items = useCartStore((state) => state.items);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const clear = useCartStore((state) => state.clear);

  // Cart state - RFQ context
  const projectId = useCartStore((state) => state.projectId);
  const projectName = useCartStore((state) => state.projectName);
  const isNewProject = useCartStore((state) => state.isNewProject);
  const setProject = useCartStore((state) => state.setProject);
  const setNewProject = useCartStore((state) => state.setNewProject);
  const vendorIds = useCartStore((state) => state.vendorIds);
  const vendorNames = useCartStore((state) => state.vendorNames);
  const addVendor = useCartStore((state) => state.addVendor);
  const removeVendor = useCartStore((state) => state.removeVendor);
  const syncVendorsFromItems = useCartStore((state) => state.syncVendorsFromItems);
  const requestedDate = useCartStore((state) => state.requestedDate);
  const setRequestedDate = useCartStore((state) => state.setRequestedDate);
  const rfqDeadline = useCartStore((state) => state.rfqDeadline);
  const setRfqDeadline = useCartStore((state) => state.setRfqDeadline);
  const notes = useCartStore((state) => state.notes);
  const setNotes = useCartStore((state) => state.setNotes);

  // Capability store — resolve strategy names
  const projectsStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'list'));
  const vendorsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_list'));
  const rfqCreateStrategy = useCapabilityStore((s) => s.strategyName('procurement', 'create_rfq'));

  // API data
  const { data: projectsData } = useProjects(projectsStrategy!, { limit: 50 });
  const { data: vendorsData, isLoading: vendorsLoading } = useVendors(vendorsStrategy!, { limit: 100 });

  // Auto-sync vendors from cart items
  useEffect(() => {
    if (items.length > 0 && vendorIds.length === 0) {
      syncVendorsFromItems();
    }
  }, [items.length, vendorIds.length, syncVendorsFromItems]);

  // Filtered lists for dropdowns
  const filteredProjects = useMemo(() => {
    const projects = projectsData?.projects || [];
    if (!projectSearch) return projects.slice(0, 10);
    const search = projectSearch.toLowerCase();
    return projects.filter((p) => p.name.toLowerCase().includes(search)).slice(0, 10);
  }, [projectsData, projectSearch]);

  const filteredVendors = useMemo(() => {
    const vendors = vendorsData?.vendors || [];
    const available = vendors.filter((v) => !vendorIds.includes(v.semantic_id));
    if (!vendorSearch) return available.slice(0, 10);
    const search = vendorSearch.toLowerCase();
    return available.filter((v) => v.name.toLowerCase().includes(search)).slice(0, 10);
  }, [vendorsData, vendorSearch, vendorIds]);

  // RFQ creation
  const createRFQ = useCreateRFQ(rfqCreateStrategy!);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      setShowProjectDropdown(false);
      setShowVendorDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSubmit = async () => {
    if (items.length === 0) return;

    setIsSubmitting(true);

    try {
      const result = await createRFQ.mutateAsync({
        line_items: items.map((item) => ({
          product_id: item.product_sid,
          quantity: item.quantity,
          description: item.name,
        })),
        project_id: isNewProject ? projectName : (projectId || undefined),
        vendor_ids: vendorIds.length > 0 ? vendorIds : undefined,
        requested_date: requestedDate || undefined,
        rfq_deadline: rfqDeadline || undefined,
        notes: notes || undefined,
      });

      if (result.success) {
        toast.success(`RFQ ${result.rfq_reference} created`, {
          description: 'Your request for quote has been submitted',
        });
        clear();
      } else {
        toast.error('Failed to create RFQ', {
          description: result.error || 'Unknown error',
        });
      }
    } catch (error) {
      toast.error('Failed to create RFQ', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const estimatedTotal = items.reduce(
    (sum, item) => sum + (getItemPrice(item) || 0) * item.quantity,
    0
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4 bg-muted/30">
        <div className="flex items-center gap-3">
          <ShoppingCart className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">RFQ Cart</h1>
          {items.length > 0 && (
            <span className="rounded-full bg-primary px-2.5 py-0.5 text-sm text-primary-foreground">
              {totalItems} items
            </span>
          )}
        </div>
        {items.length > 0 && (
          <Button variant="outline" size="sm" onClick={clear}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Cart
          </Button>
        )}
      </div>

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Package className="mb-4 h-16 w-16 text-muted-foreground/30" />
            <p className="text-lg text-muted-foreground">Your cart is empty</p>
            <p className="mt-2 text-sm text-muted-foreground/60">
              Browse vendor catalogs and add products to create an RFQ
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-8">
            {/* Products Grid */}
            <section>
              <h2 className="text-sm font-medium text-muted-foreground mb-4">
                Products ({items.length})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {items.map((item) => (
                  <ProductCard
                    key={item.line_id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeItem}
                  />
                ))}
              </div>
            </section>

            {/* RFQ Details Form */}
            <section className="border-t border-border pt-6">
              <h2 className="text-sm font-medium text-muted-foreground mb-4">
                RFQ Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Project */}
                <div className="relative">
                  <label className="mb-2 block text-sm font-medium">
                    <FolderOpen className="mr-1.5 inline-block h-4 w-4" />
                    Project
                  </label>
                  {projectName ? (
                    <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{projectName}</span>
                        {isNewProject && (
                          <span className="rounded bg-primary/20 px-1.5 py-0.5 text-xs text-primary">NEW</span>
                        )}
                      </div>
                      <button
                        onClick={() => setProject(null, null)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Input
                        value={projectSearch}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setProjectSearch(e.target.value);
                          setShowProjectDropdown(true);
                        }}
                        onFocus={() => setShowProjectDropdown(true)}
                        placeholder="Search or create..."
                        className="bg-background"
                      />
                      {showProjectDropdown && (
                        <div
                          className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="max-h-48 overflow-auto p-1">
                            {filteredProjects.map((project) => (
                              <button
                                key={project.semantic_id}
                                onClick={() => {
                                  setProject(project.semantic_id, project.name);
                                  setProjectSearch('');
                                  setShowProjectDropdown(false);
                                }}
                                className="w-full rounded px-3 py-2 text-left text-sm hover:bg-accent"
                              >
                                {project.name}
                              </button>
                            ))}
                            {projectSearch && (
                              <button
                                onClick={() => {
                                  setNewProject(projectSearch);
                                  setProjectSearch('');
                                  setShowProjectDropdown(false);
                                }}
                                className="w-full rounded px-3 py-2 text-left text-sm hover:bg-accent border-t border-border mt-1"
                              >
                                <Plus className="mr-1.5 inline-block h-4 w-4 text-primary" />
                                <span className="text-primary">Create &quot;{projectSearch}&quot;</span>
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Vendors */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">
                      <Users className="mr-1.5 inline-block h-4 w-4" />
                      Vendors ({vendorIds.length})
                    </label>
                    <button
                      onClick={syncVendorsFromItems}
                      className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                    >
                      <RefreshCw className="h-3 w-3" />
                      Sync
                    </button>
                  </div>
                  {vendorIds.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {vendorNames.map((name, idx) => (
                        <span
                          key={vendorIds[idx]}
                          className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"
                        >
                          {name}
                          <button
                            onClick={() => removeVendor(vendorIds[idx])}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Input
                      value={vendorSearch}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setVendorSearch(e.target.value);
                        setShowVendorDropdown(true);
                      }}
                      onFocus={() => setShowVendorDropdown(true)}
                      placeholder="Add vendor..."
                      className="bg-background"
                    />
                    {showVendorDropdown && (
                      <div
                        className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="max-h-48 overflow-auto p-1">
                          {vendorsLoading ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">Loading...</div>
                          ) : filteredVendors.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              {vendorSearch ? `No vendors matching "${vendorSearch}"` : 'No more vendors'}
                            </div>
                          ) : (
                            filteredVendors.map((vendor) => (
                              <button
                                key={vendor.semantic_id}
                                onClick={() => {
                                  addVendor(vendor.semantic_id, vendor.name, vendor.email);
                                  setVendorSearch('');
                                  setShowVendorDropdown(false);
                                }}
                                className="w-full rounded px-3 py-2 text-left text-sm hover:bg-accent"
                              >
                                {vendor.name}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Date */}
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    <Calendar className="mr-1.5 inline-block h-4 w-4" />
                    Delivery Date
                  </label>
                  <Input
                    type="date"
                    value={requestedDate || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRequestedDate(e.target.value || null)}
                    className="bg-background"
                  />
                </div>

                {/* RFQ Deadline */}
                <div>
                  <label className="mb-2 block text-sm font-medium">
                    <Calendar className="mr-1.5 inline-block h-4 w-4" />
                    Response Deadline
                  </label>
                  <Input
                    type="date"
                    value={rfqDeadline || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRfqDeadline(e.target.value || null)}
                    className="bg-background"
                  />
                </div>
              </div>

              {/* Notes - Full Width */}
              <div className="mt-6">
                <label className="mb-2 block text-sm font-medium">
                  <FileText className="mr-1.5 inline-block h-4 w-4" />
                  Notes (optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes for this RFQ..."
                  className="h-24 resize-none bg-background"
                />
              </div>
            </section>

          </div>
        )}
      </div>

      {/* Footer - Summary & Submit */}
      {items.length > 0 && (
        <div className="border-t border-border px-6 py-4 bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-sm text-muted-foreground">Products</span>
                <p className="text-lg font-semibold">{items.length}</p>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Total Units</span>
                <p className="text-lg font-semibold">{totalItems}</p>
              </div>
              {estimatedTotal > 0 && (
                <div>
                  <span className="text-sm text-muted-foreground">Estimated Value</span>
                  <p className="text-lg font-semibold">${estimatedTotal.toLocaleString()}</p>
                </div>
              )}
            </div>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || items.length === 0}
              size="lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Creating RFQ...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-5 w-5" />
                  Submit RFQ
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Product card for grid display
function ProductCard({
  item,
  onUpdateQuantity,
  onRemove,
}: {
  item: CartItem;
  onUpdateQuantity: (line_id: string, quantity: number) => void;
  onRemove: (line_id: string) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden group">
      {/* Image */}
      <div className="relative aspect-square bg-muted">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        {/* Remove button overlay */}
        <button
          onClick={() => onRemove(item.line_id)}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-background/80 text-muted-foreground hover:text-destructive hover:bg-background opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm font-medium line-clamp-2 mb-1">{item.name}</p>
        {getItemVendorName(item) && (
          <p className="text-xs text-muted-foreground mb-2">{getItemVendorName(item)}</p>
        )}

        {/* Quantity & Price */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center bg-muted rounded-md shrink-0">
            <button
              onClick={() => onUpdateQuantity(item.line_id, item.quantity - 1)}
              className="h-6 w-6 flex items-center justify-center rounded-l-md hover:bg-accent transition-colors"
            >
              <Minus className="h-3 w-3" />
            </button>
            <input
              type="number"
              value={item.quantity}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                onUpdateQuantity(item.line_id, Math.max(1, val));
              }}
              className="w-8 h-6 text-center text-xs font-medium bg-transparent border-0 focus:outline-none"
              min="1"
            />
            <button
              onClick={() => onUpdateQuantity(item.line_id, item.quantity + 1)}
              className="h-6 w-6 flex items-center justify-center rounded-r-md hover:bg-accent transition-colors"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>

          {getItemPrice(item) && (
            <span className="text-sm font-medium truncate" title={`$${((getItemPrice(item) || 0) * item.quantity).toLocaleString()}`}>
              ${((getItemPrice(item) || 0) * item.quantity).toLocaleString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
